import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Building,
  BookOpen,
  TrendingUp,
  BarChart3,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  Bell,
  FileText,
  DollarSign,
  Database,
  Shield,
  HomeIcon,
  SlidersHorizontal
} from 'lucide-react';
import logo from '../assets/logo.png';
import logophone from '../assets/logophone.png';

interface NavigationItem {
  icon: React.ComponentType<any>;
  label: string;
  path: string;
  badge?: string;
}

const AdminLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigationItems: NavigationItem[] = [
    { icon: LayoutDashboard, label: t('admin.navigation.dashboard'), path: '/admin/dashboard' },
    { icon: HomeIcon, label: t('admin.navigation.backToSite'), path: '/' },
    { icon: Users, label: t('admin.navigation.professionals'), path: '/admin/users' },
    { icon: Building, label: t('admin.navigation.trainingCenters'), path: '/admin/training-centers' },
    { icon: Users, label: t('admin.navigation.consultants'), path: '/admin/consultants' },
    { icon: BookOpen, label: t('admin.navigation.trainings'), path: '/admin/trainings' },
    { icon: TrendingUp, label: t('admin.navigation.prestations'), path: '/admin/prestations' },
    { icon: DollarSign, label: t('admin.navigation.subscriptions'), path: '/admin/subscriptions' },
    { icon: FileText, label: t('admin.navigation.invoices'), path: '/admin/invoices' },
    { icon: BarChart3, label: t('admin.navigation.reports'), path: '/admin/reports' },
    { icon: Shield, label: t('admin.navigation.moderation'), path: '/admin/moderation' },
    { icon: Database, label: t('admin.navigation.dataManagement'), path: '/admin/data-management' },
    { icon: SlidersHorizontal, label: 'Sliders', path: '/admin/sliders' },
    { icon: Settings, label: t('admin.navigation.settings'), path: '/admin/settings' },
  ];

  const isActivePath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-brand-surface flex">
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-label={t('admin.navigation.closeMenu')}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed z-30 inset-y-0 left-0 bg-white shadow-2xl max-h-screen w-64 transition-all duration-300 border-r border-gray-200 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Header Sidebar */}
        <div className="flex flex-col items-center py-8 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-2 mb-4">
            <img
              src={logophone}
              alt="PHINLEX Logo"
              className="h-14 w-14 rounded-full object-cover border-2 border-brand-primary/20 shadow-md"
            />
          </div>
         
          <p className="text-sm text-gray-600 mt-1">{t('admin.navigation.administration')}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                  isActive
                    ? 'bg-brand-primary/10 text-brand-primary border border-brand-primary/20'
                    : 'text-gray-700 hover:bg-brand-primary/5 hover:text-brand-primary'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${isActive ? 'text-brand-primary' : 'text-gray-500'}`} />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.badge && (
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.email || 'Admin'}
              </p>
              <p className="text-xs text-gray-500">{t('admin.navigation.administration')}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-lg transition-all duration-200"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">{t('admin.navigation.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-brand-primary/10 transition-colors"
              >
                {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                {t('admin.navigation.title')}
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              {/* User Menu */}
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.email || 'Admin'}
                  </p>
                  <p className="text-xs text-gray-500">{t('admin.navigation.administration')}</p>
                </div>
                <div className="h-8 w-8 bg-brand-primary/10 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-brand-primary" />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
